import { 
  type Hero1Card, 
  Hero1CardSchema, 
  type Hero2Card, 
  Hero2CardSchema, 
  type Hero3Card, 
  Hero3CardSchema,
  type Hero4Card,
  Hero4CardSchema,
  type Hero5Card,
  Hero5CardSchema,
  type SocialMediaLink 
} from "@stackje/common/config";
import { ValidationError, getStringFromFormData, getOptionalStringFromFormData } from "./validation";

export function parseHeroCard(formData: FormData): Hero1Card | Hero2Card | Hero3Card | Hero4Card | Hero5Card {
  const type = formData.get("type") as "hero1" | "hero2" | "hero3" | "hero4" | "hero5";
  
  // Parse social links - check for JSON string first, then individual fields
  let socialLinks: SocialMediaLink[] = [];
  const socialLinksJson = formData.get("socialLinks") as string;
  
  if (socialLinksJson && socialLinksJson !== "undefined") {
    try {
      socialLinks = JSON.parse(socialLinksJson);
    } catch {
      // Fallback to individual fields if JSON parsing fails
    }
  }
  
  // Fallback to individual fields if JSON parsing failed or no JSON found
  if (socialLinks.length === 0) {
    let index = 0;
    while (formData.get(`socialPlatform${index}`) !== null) {
      const platform = formData.get(`socialPlatform${index}`) as string;
      const url = formData.get(`socialUrl${index}`) as string;
      const displayName = formData.get(`socialDisplayName${index}`) as string;
      
      if (platform && url) {
        socialLinks.push({
          platform: platform as SocialMediaLink["platform"],
          url,
          displayName: displayName || undefined,
        });
      }
      index++;
    }
  }

  const baseCard = {
    colors: JSON.parse(formData.get("colors") as string),
    brandTitle: getOptionalStringFromFormData(formData, "brandTitle"),
    title: getStringFromFormData(formData, "title"),
    description: getStringFromFormData(formData, "description"),
    socialLinks,
    ...(formData.get("buttonText") &&
      formData.get("buttonUrl") && {
        button: {
          title: getStringFromFormData(formData, "buttonText"),
          url: getStringFromFormData(formData, "buttonUrl"),
        },
      }),
  };

  // Since all hero types now have unified schema, we can parse all fields consistently
  const cardData = {
    ...baseCard,
    type: type as "hero1" | "hero2" | "hero3" | "hero4" | "hero5",
    // Parse logo field (used by all hero types)
    logo: formData.get("logo") && formData.get("logo") !== ""
      ? {
          src: getStringFromFormData(formData, "logo"),
          alt: getStringFromFormData(formData, "logoAlt", baseCard.brandTitle || "Brand logo"),
        }
      : undefined,
    // Parse image field (used by Hero2, Hero3, Hero4, Hero5)
    image: formData.get("image") && formData.get("image") !== ""
      ? {
          src: getStringFromFormData(formData, "image"),
          alt: getStringFromFormData(formData, "imageAlt", baseCard.title || "Image"),
        }
      : undefined,
    // Parse background image field (used by Hero3, Hero4)  
    backgroundImage: formData.get("backgroundImage") && formData.get("backgroundImage") !== ""
      ? {
          src: getStringFromFormData(formData, "backgroundImage"),
          alt: "Background image",
        }
      : undefined,
    diagonalAngle: formData.get("diagonalAngle") 
      ? parseInt(formData.get("diagonalAngle") as string) 
      : 20,
  };

  
  try {
    // With unified schema, we can use a single validation approach
    let parsedCard;
    if (type === "hero1") {
      parsedCard = Hero1CardSchema.parse(cardData);
    } else if (type === "hero2") {
      parsedCard = Hero2CardSchema.parse(cardData);
    } else if (type === "hero3") {
      parsedCard = Hero3CardSchema.parse(cardData);
    } else if (type === "hero4") {
      parsedCard = Hero4CardSchema.parse(cardData);
    } else {
      parsedCard = Hero5CardSchema.parse(cardData);
    }
    
    return parsedCard;
  } catch (error) {
    if (error instanceof Error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      throw new ValidationError(error as any);
    }
    throw error;
  }
}
